import { SolValidationBuilder } from './sol-validation-builder';
import { SolText } from './sol-text';

export interface SolValidatorConfig {}

export interface SolValidatorNameConfig extends SolValidatorConfig {
  name: string;
}

export interface SolValidatorLengthConfig extends SolValidatorNameConfig {
  length: number;
}

export interface SolValidatorPatternConfig extends SolValidatorNameConfig {
  pattern: string;
}

export interface SolValidatorBoundConfig extends SolValidatorNameConfig {
  bound: number;
}

export interface SolValidator<V> {
  validate(builder: SolValidationBuilder, value: V | undefined, path: string): void;
}

/**
 * Validator function for validating a domain object (and the associated sol object definition).
 */
export class SolValidatorDef<V, C extends SolValidatorConfig> {
  public static readonly required = SolValidatorDef.of<any, SolValidatorNameConfig>('required', config => ({
    validate: (builder: SolValidationBuilder, value: any | undefined, path: string): void => {
      if (value === undefined || (typeof value === 'string' && value.trim() === '')) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.required`, { name: config.name }),
        });
      }
    },
  }));

  public static readonly maxLength = SolValidatorDef.of<string, SolValidatorLengthConfig>('maxLength', config => ({
    validate: (builder: SolValidationBuilder, value: string | undefined, path: string): void => {
      const valueLength = value?.length ?? 0;
      if (valueLength > config.length) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.maxLength`, {
            name: config.name,
            length: config.length,
          }),
        });
      }
    },
  }));

  public static readonly minLength = SolValidatorDef.of<string, SolValidatorLengthConfig>('minLength', config => ({
    validate: (builder: SolValidationBuilder, value: string | undefined, path: string): void => {
      const valueLength = value?.length ?? 0;
      if (valueLength < config.length) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.minLength`, {
            name: config.name,
            length: config.length,
          }),
        });
      }
    },
  }));

  public static readonly pattern = SolValidatorDef.of<string, SolValidatorPatternConfig>('pattern', config => ({
    validate: (builder: SolValidationBuilder, value: string | undefined, path: string): void => {
      if (!value?.match(config.pattern)) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.pattern`, { name: config.name }),
        });
      }
    },
  }));

  public static readonly email = SolValidatorDef.of<string, SolValidatorNameConfig>('email', config => ({
    validate: (builder: SolValidationBuilder, value: string | undefined, path: string): void => {
      const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!value || !expression.test(value)) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.email`, { name: config.name }),
        });
      }
    },
  }));

  public static readonly min = SolValidatorDef.of<number, SolValidatorBoundConfig>('min', config => ({
    validate: (builder: SolValidationBuilder, value: number | undefined, path: string): void => {
      if (value !== undefined && value < config.bound) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.min`, { name: config.name, bound: config.bound }),
        });
      }
    },
  }));

  public static readonly max = SolValidatorDef.of<number, SolValidatorBoundConfig>('max', config => ({
    validate: (builder: SolValidationBuilder, value: number | undefined, path: string): void => {
      if (value !== undefined && value > config.bound) {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.max`, { name: config.name, bound: config.bound }),
        });
      }
    },
  }));

  /** URL validator — empty passes; non-empty must parse via the WHATWG URL parser. */
  public static readonly url = SolValidatorDef.of<string, SolValidatorNameConfig>('url', config => ({
    validate: (builder: SolValidationBuilder, value: string | undefined, path: string): void => {
      if (!value) {
        return;
      }
      try {
        new URL(value);
      } catch {
        builder.add({
          path,
          type: 'error',
          text: SolText.of(`sol-validator.url`, { name: config.name }),
        });
      }
    },
  }));

  private constructor(
    public readonly id: string,
    public readonly toInstance: (config: C) => SolValidator<V>,
  ) {}

  private static of<V, C extends SolValidatorConfig>(
    id: string,
    factory: (config: C) => SolValidator<V>,
  ): SolValidatorDef<V, C> {
    return new SolValidatorDef<V, C>(id, factory);
  }

  public of(config: C): SolValidator<V> {
    return this.toInstance(config);
  }
}
